// AI Studio Types

import { videoGenerationModelsQuery$data } from './queries/__generated__/videoGenerationModelsQuery.graphql';
import { modelGeneration3DModelsQuery$data } from './queries/__generated__/modelGeneration3DModelsQuery.graphql';

export type ToolType =
    | 'virtual-tryon'
    | 'magic-swap'
    | 'product-staging'
    | 'multi-image-fusion'
    | 'ad-generator'
    | 'video-generation'
    | 'upscaler'
    | 'text-to-3d'
    | 'image-to-3d'
    | 'multi-views-to-3d';

export const MODEL3D_TOOL_IDS: ToolType[] = ['text-to-3d', 'image-to-3d', 'multi-views-to-3d'];

export interface GenerationResult {
    id: string;
    url: string;
    timestamp: Date;
    tool: ToolType;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    error?: string;
}

export interface VideoResult extends GenerationResult {
    videoUrl?: string;
    duration?: number;
}

// Tool configuration
export interface ToolConfig {
    id: ToolType;
    name: string;
    /** Description of the tool */
    description?: string;
    /** The preset type ID that accepts the input image (product image or 3D capture) */
    inputPresetType?: string;
    /** Link to the guide/help documentation for this tool */
    guideLink?: string;
    /** Placeholder text for the prompt input */
    promptPlaceholder?: string;
    /** Whether this tool supports product catalog selection (default: true) */
    allowProductMode?: boolean;
    /** Whether this tool supports prompt input (default: true) */
    allowPrompt?: boolean;
    /** Whether this tool supports image input (default: true) */
    allowImageInput?: boolean;
    /** Duration in seconds for the fake progress bar animation (default: 120) */
    fakeDuration?: number;
}

// Asset generation settings stored as metadata JSON
export interface AssetGenerationSettings {
    // Common
    toolId?: string;
    toolName?: string;
    prompt?: string;
    negativePrompt?: string;
    model?: string;
    width?: number;
    height?: number;
    seed?: number;
    strength?: number;
    steps?: number;
    guidanceScale?: number;
    sourceImageUrl?: string;
    styleImageUrl?: string;
    maskImageUrl?: string;

    // Tool-specific presets
    backgroundPreset?: string;
    backgroundImageUrl?: string;
    personPreset?: string;
    personImageUrl?: string;
    clothingPreset?: string;
    clothingImageUrl?: string;
    posePreset?: string;
    effectPreset?: string;
    surfacePreset?: string;
    moodPreset?: string;
    materialPreset?: string;
    colorPreset?: string;
    layoutPreset?: string;
    formatPreset?: string;
    stylePreset?: string;
    videoPreset?: string;

    // Upscaler
    scaleFactor?: number;

    // Video generation
    durationSeconds?: number;
    resolution?: string;
    aspectRatio?: string;
    generateAudio?: boolean;

    // Ad generator
    language?: string;
    title?: string;
    subtitle?: string;
    ctaText?: string;

    // System
    savedAt?: string;
    originalUrl?: string;
    model3DParameters?: Record<string, string | number | boolean | readonly number[] | null | undefined>;
}

// Product side image from customizer
export interface ProductSideImage {
    sideId: number;
    sideName?: string;
    url: string;
    file?: File;
}

// Input image data (user-uploaded, from results, asset library, 3D capture, or side images)
export interface InputImageData {
    id: string;
    url: string;
    name?: string;
    /** Whether this image came from a product (catalog/3D capture) and should not be removable */
    fromProduct?: boolean;
}

// Re-export SelectionValue for convenience
export type { SelectionValue } from './components/presets/presetOptionButtons';

// Preset selection state for the bottom panel
export interface PresetSelectionState {
    /** Map of selections: key is typeId, value is the selected preset info (or array for multiSelect types) */
    selections: Record<string, import('./components/presets/presetOptionButtons').SelectionValue>;
}

// Video generation specific options
type VideoGenerationModel = videoGenerationModelsQuery$data['videoGenerationModels'][number];
export type ModelGeneration3DModel = modelGeneration3DModelsQuery$data['modelGeneration3DModels'][number];
export type Model3DModelSelections = Partial<Record<(typeof MODEL3D_TOOL_IDS)[number], string>>;

export interface VideoOptions {
    duration: VideoGenerationModel['allowedDurations'][number];
    model: VideoGenerationModel['model'];
    resolution: VideoGenerationModel['allowedResolutions'][number];
    aspectRatio: VideoGenerationModel['allowedAspectRatios'][number] | undefined;
    generateAudio: boolean;
}

// Ad generator specific options
export interface AdOptions {
    language: string;
    title: string;
    subtitle: string;
    ctaText: string;
}

// Available languages for ad generator
export const AD_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'it', label: 'Italiano' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'pt', label: 'Português' }
];
